import React from 'react';
import {
  type ContextualExpression,
  stripAll,
} from '@packages/utilities/contextual-expression';
import {
  TList,
  TElement,
  type ExpressionElement,
  type Expression,
} from '@packages/systems/wfdl-ast';
import {
  TypeImplementation,
  Expressions,
  Component,
} from '@packages/systems/wfdl';
import {
  selectComponents,
  selectFocusedSymbolNativeIdPath,
  selectSelectedNativeIdPath,
  getPluginsFromState,
} from '@packages/utilities/platform-selectors';
import {getNativeIdPathToSlotDefault} from '@packages/utilities/native-id-paths';
import {
  type NodeHoveredPayload,
  type NodeClickedPayload,
  getNodeHoveredActionCreator,
  getNodeClickedActionCreator,
} from '@packages/systems/designer/canvas';
import PropertyList, {
  PropertyListItem,
} from '@packages/systems/ui/shared-ui/PropertyList';
import {
  InfoIcon,
  Note,
  NoteBody,
  View,
  Text,
  tokens,
} from '@packages/systems/spring';
import {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {iconName, qualify} from '@packages/systems/wfdl-Qualified';
import {
  useDesignerSelector,
  useDispatch,
} from '@packages/systems/designer/flux';
// Can't relocate to platform-selectors either: this selector needs selectDesignerAbility,
// and designer-abilities already imports from platform-selectors, so moving it there
// would create a platform-selectors <-> designer-abilities cycle.
// eslint-disable-next-line webflow/module-top-level-imports -- selectors are pure store logic; the ContentEditorUi barrel also exports ContentEditorStoreUtils (`marked`) and ContentEditor.tsx, which must not enter the plugins/Slots → react-renderer lambda graph
import {selectIsActiveComponentInstanceInRichText} from '@packages/systems/ContentEditorUi/selectors';

const emptyViewStyles = {
  border: '$border1',
  padding: '$1',
  borderRadius: '$2',
  color: '$textSecondary',
  textAlign: 'center',
  height: '$7',
} as const;

/**
 * Convenience hook to create a `nodeHovered` action creator in functional React
 * components that will not require manually passing the dependencies
 */
type ActionCreator = (
  componentMap: Component.ComponentMap,
  focusedSymbolNativeIdPath: NativeId[]
) => (payload: NodeClickedPayload | NodeHoveredPayload) => any;

const useNodeAction = (getActionCreator: ActionCreator) => {
  const componentMap = useDesignerSelector(selectComponents);
  const focusedSymbolNativeIdPath = useDesignerSelector(
    selectFocusedSymbolNativeIdPath
  );

  return React.useCallback(
    (payload: NodeClickedPayload | NodeHoveredPayload) =>
      getActionCreator(componentMap, focusedSymbolNativeIdPath)(payload),
    [componentMap, focusedSymbolNativeIdPath, getActionCreator]
  );
};

const useSlotDefaultNativeIdPath = (el: ExpressionElement | null) => {
  const componentMap = useDesignerSelector(selectComponents);
  const plugins = useDesignerSelector(getPluginsFromState);
  const selectedNativeIdPath = useDesignerSelector(selectSelectedNativeIdPath);

  return React.useCallback(
    () =>
      getNativeIdPathToSlotDefault(
        el,
        componentMap,
        selectedNativeIdPath,
        plugins
      ),
    [componentMap, el, plugins, selectedNativeIdPath]
  );
};

/*
 * Component to render when there ARE NO Slot override elements.
 */
const Empty = () => (
  <View {...emptyViewStyles}>
    <Text data-automation-id="Empty-SlotContent">Empty</Text>
  </View>
);

/*
 * Component to render in place of the Slot content control when the
 * component instance is inside Rich Text, where Slots can't be populated.
 */
const RichTextSlotNote = () => (
  <View data-automation-id="component-instance-rich-text-slot-note">
    <Note icon={InfoIcon} isInline>
      <NoteBody>{"Component slots can't be populated in Rich Text."}</NoteBody>
    </Note>
  </View>
);

/*
 * Component to render when there ARE Slot override elements.
 */
const ElementCount = ({list}: {list: Expression[]}) => {
  const nodeClicked = useNodeAction(getNodeClickedActionCreator);
  const nodeHovered = useNodeAction(getNodeHoveredActionCreator);

  const dispatch = useDispatch();

  // @ts-expect-error - TS2345 - Argument of type 'Expression | undefined' is not assignable to parameter of type 'ExpressionF<Expression>'.
  const el: ExpressionElement | null = Expressions.getElement(list[0]);

  const getSlotDefaultPath = useSlotDefaultNativeIdPath(el);

  if (!el) return <Empty />;

  const count = list.length;
  const elementCount = count === 1 ? '1 element' : `${count} elements`;
  const items = [{item: el, id: el.id}];

  const handleEvent = (
    eventType: (payload: NodeClickedPayload | NodeHoveredPayload) => any,
    id: string | null
  ) => {
    const slotDefaultNativeIdPath = id ? getSlotDefaultPath() : null;

    if (slotDefaultNativeIdPath) {
      dispatch(eventType({nativeIdPath: slotDefaultNativeIdPath}));
      return;
    }

    dispatch(eventType({nativeIdInCurrentComponent: id}));
  };

  const label = (
    <>
      <Text style={{color: tokens.colors.actionSecondaryText}}>Content: </Text>
      <Text style={{color: tokens.colors.textSecondary}}>{elementCount}</Text>
    </>
  );

  return (
    <PropertyList
      sortable={false}
      disabled={false}
      items={items}
      renderItem={(listItem) => (
        <PropertyListItem
          key={listItem.id}
          id={listItem.id}
          label={label}
          onClick={() => handleEvent(nodeClicked, el.id)}
          onMouseOver={() => handleEvent(nodeHovered, el.id)}
          onMouseOut={() => handleEvent(nodeHovered, null)}
          onFocus={() => handleEvent(nodeHovered, el.id)}
          onBlur={() => handleEvent(nodeHovered, null)}
          style={{paddingLeft: 0, height: tokens.space[7]}}
        />
      )}
      shouldShowTargetIcon
    />
  );
};

/**
 *  The `SlotContent` Type is a list of elements that simply renders a default
 *  ExpressionEditor (for now). We're using this Alias to represent Slot fields
 *  so we can distinguish between Slot content and other kinds of content that
 *  are also of Type List<Elements> (ie. like data warehouse queries).
 */
export const SlotContent = TypeImplementation.Alias({
  type: TList(TElement),
  system: {
    displayName: 'Slot Content',
  },
  icon: TypeImplementation.IconByName(qualify('Slots', iconName('Slot'))),
  render: function RenderSlotContent({expr}: {expr: ContextualExpression}) {
    const isInRichText = useDesignerSelector(
      selectIsActiveComponentInstanceInRichText
    );
    const _expr = stripAll(expr);
    const list: Expression[] | null = Expressions.getList(_expr);

    if (isInRichText) {
      return <RichTextSlotNote />;
    }

    return !list || list.length === 0 ? (
      <Empty />
    ) : (
      <ElementCount list={list} />
    );
  },
});
